import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Plus, TriangleAlert } from 'lucide-react';
import { DndContext, closestCenter, type DragEndEvent, type UniqueIdentifier } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DragHandle, useReorderSensors } from '@/components/ui/reorder';
import { formatCurrency } from '@/lib/i18n';
import { api } from '@/lib/api';
import { usePermission } from '@/Hooks/usePermission';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog } from '@/components/ui/dialog';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { TD, TH, THead, TR, Table, TableWrap } from '@/components/ui/table';
import { apiError } from '../lib';
import CurrencyFormDialog from './CurrencyFormDialog';
import {
  currencyRows,
  formatAsDefined,
  isoVerdict,
  reorderCurrencies,
  type PlatformCurrency,
} from './lib';

/** The amount every row previews. One figure, so the rows are comparable. */
const ROW_SAMPLE = '1234.5678';

/**
 * One draggable table row.
 *
 * `CSS.Translate`, not `Transform`: a transform that scales would smear the row's
 * digits, and this table is nothing but digits. The row lifts to `zIndex: 1` while
 * it moves, or it slides underneath the rows it is passing.
 */
function SortableCurrencyRow({
  id,
  draggable,
  handleLabel,
  children,
}: {
  id: string;
  draggable: boolean;
  handleLabel: string;
  children: (handle: ReactNode) => ReactNode;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: !draggable });

  return (
    <TR
      ref={setNodeRef}
      style={{
        transform: CSS.Translate.toString(transform),
        transition,
        position: 'relative',
        zIndex: isDragging ? 1 : undefined,
      }}
      className={isDragging ? 'opacity-60' : undefined}
    >
      {children(
        draggable ? (
          <DragHandle
            ref={setActivatorNodeRef}
            label={handleLabel}
            dragging={isDragging}
            {...attributes}
            {...listeners}
          />
        ) : (
          // The cell is kept even when dragging is off, so clearing the search
          // does not shift every column sideways.
          <span className="inline-block h-8 w-7" aria-hidden />
        ),
      )}
    </TR>
  );
}

/**
 * Currencies — GLOBAL platform reference data, which is why it lives here and
 * not in a tenant's dashboard. A tenant picks a base currency from this list;
 * it never invents one, because two platforms disagreeing on KWD's minor unit
 * corrupts every cross-tenant figure.
 *
 * Rows are retired (`DELETE` deactivates), never erased: tenants, branches,
 * price lists and every historical amount still point at them.
 */
export default function Currencies() {
  const { t, i18n } = useTranslation();
  const { can } = usePermission();
  const canManage = can('platform.manage_plans');

  const [rows, setRows] = useState<PlatformCurrency[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [showRetired, setShowRetired] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<PlatformCurrency | null>(null);
  const [retiring, setRetiring] = useState<PlatformCurrency | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    // `activeOnly` is sent explicitly in both directions rather than omitted,
    // so the retired rows appear regardless of what the endpoint defaults to.
    api
      .get<unknown>('/currencies', { params: { activeOnly: !showRetired } })
      .then(({ data }) => setRows(currencyRows(data)))
      .catch((e) => setError(apiError(e, t('currency.admin.loadFailed'))))
      .finally(() => setLoading(false));
  }, [showRetired, t]);

  useEffect(load, [load]);

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return rows
      .filter((row) => showRetired || row.isActive)
      .filter(
        (row) =>
          !needle ||
          row.code.toLowerCase().includes(needle) ||
          row.name.toLowerCase().includes(needle),
      )
      .slice()
      .sort((a, b) => a.sortOrder - b.sortOrder || a.code.localeCompare(b.code));
  }, [rows, search, showRetired]);

  const mismatches = useMemo(
    () => visible.filter((row) => isoVerdict(row.code, row.decimals).mismatch).length,
    [visible],
  );

  const retire = async () => {
    if (!retiring) return;
    try {
      await api.delete(`/currencies/${retiring.id}`);
      toast.success(t('currency.admin.retired'));
      setRetiring(null);
      load();
    } catch (e) {
      toast.error(apiError(e, t('currency.admin.saveFailed')));
    }
  };

  // --- reordering ------------------------------------------------------------

  const sensors = useReorderSensors();

  /**
   * Dragging is off while a search is running. A filtered list hides rows, so an
   * order dragged out of it is not the order the operator saw — and the write below
   * numbers the whole visible list from zero, which would silently renumber over the
   * rows the filter removed.
   *
   * Retired rows are a different case and deliberately not blocked: `sortOrder` only
   * governs the order tenants pick from, and a retired currency is not in any picker.
   */
  const canReorder = canManage && search.trim() === '';

  const positionOf = useCallback(
    (id: UniqueIdentifier) => {
      const index = visible.findIndex((row) => row.id === id);
      return {
        name: visible[index]?.code ?? '',
        index: index + 1,
        count: visible.length,
      };
    },
    [visible],
  );

  /**
   * Announcements are not decoration here: with the pointer sensor the operator can
   * see the gap open, and with the keyboard sensor they cannot see anything at all.
   * These sentences are the entire feedback channel for a keyboard reorder.
   */
  const accessibility = useMemo(
    () => ({
      screenReaderInstructions: { draggable: t('currency.admin.reorder.instructions') },
      announcements: {
        onDragStart: ({ active }: { active: { id: UniqueIdentifier } }) =>
          t('currency.admin.reorder.picked', positionOf(active.id)),
        onDragOver: ({ over }: { over: { id: UniqueIdentifier } | null }) =>
          over ? t('currency.admin.reorder.movedOver', positionOf(over.id)) : undefined,
        onDragEnd: ({ active }: { active: { id: UniqueIdentifier } }) =>
          t('currency.admin.reorder.dropped', positionOf(active.id)),
        onDragCancel: ({ active }: { active: { id: UniqueIdentifier } }) =>
          t('currency.admin.reorder.cancelled', positionOf(active.id)),
      },
    }),
    [positionOf, t],
  );

  const onDragEnd = async ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;

    const from = visible.findIndex((row) => row.id === active.id);
    const to = visible.findIndex((row) => row.id === over.id);
    if (from < 0 || to < 0) return;

    const moved = reorderCurrencies(visible, from, to);
    const byId = new Map(moved.map((row) => [row.id, row.sortOrder]));

    // Optimistic: the row stays where it was dropped while the write is in flight,
    // and `load()` in the catch is what puts it back if the write is refused.
    const previous = rows;
    setRows(rows.map((row) => (byId.has(row.id) ? { ...row, sortOrder: byId.get(row.id)! } : row)));

    try {
      await api.post('/currencies/reorder', {
        items: moved.map(({ id, sortOrder }) => ({ id, sortOrder })),
      });
      toast.success(t('currency.admin.reorder.saved'));
    } catch (e) {
      setRows(previous);
      toast.error(apiError(e, t('currency.admin.reorder.saveFailed')));
    }
  };

  const restore = async (row: PlatformCurrency) => {
    try {
      await api.patch(`/currencies/${row.id}`, { isActive: true });
      toast.success(t('currency.admin.restored'));
      load();
    } catch (e) {
      toast.error(apiError(e, t('currency.admin.saveFailed')));
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-lg font-semibold tracking-tight">{t('currency.title')}</h1>
        <p className="text-sm text-[var(--muted-foreground)]">{t('currency.subtitle')}</p>
      </div>

      {!canManage && (
        <p className="rounded-md border border-[var(--border)] bg-[var(--muted)] p-3 text-sm text-[var(--muted-foreground)]">
          {t('currency.admin.readOnly')}
        </p>
      )}

      <Card>
        <CardHeader className="flex-row flex-wrap items-center justify-between gap-2">
          <CardTitle>{t('currency.title')}</CardTitle>
          <div className="flex flex-wrap items-center gap-2">
            <Input
              className="w-full sm:w-56"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('currency.admin.searchPlaceholder')}
              aria-label={t('common.search')}
            />
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="h-4 w-4"
                checked={showRetired}
                onChange={(e) => setShowRetired(e.target.checked)}
              />
              {t('currency.admin.showRetired')}
            </label>
            {canManage && (
              <Button
                onClick={() => {
                  setEditing(null);
                  setFormOpen(true);
                }}
              >
                <Plus className="h-4 w-4" />
                {t('currency.admin.create')}
              </Button>
            )}
          </div>
        </CardHeader>

        <CardContent className="p-0">
          {loading ? (
            <LoadingState label={t('currency.loading')} />
          ) : error ? (
            <ErrorState message={error} onRetry={load} />
          ) : visible.length === 0 ? (
            <EmptyState title={t('currency.admin.empty')} hint={t('currency.admin.emptyHint')} />
          ) : (
            <>
              {mismatches > 0 && (
                <div className="mx-5 mb-3 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-amber-900">
                  <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
                  <p className="text-xs">
                    <span className="font-semibold">{t('currency.iso.mismatchTitle')}</span>{' '}
                    {t('currency.iso.mismatchSummary', { n: mismatches })}
                  </p>
                </div>
              )}
              {canReorder && (
                <p className="mx-5 mb-3 text-xs text-[var(--muted-foreground)]">
                  {t('currency.admin.reorder.hint')}
                </p>
              )}
              {canManage && !canReorder && (
                <p className="mx-5 mb-3 text-xs text-[var(--muted-foreground)]">
                  {t('currency.admin.reorder.searchOff')}
                </p>
              )}
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                modifiers={[restrictToVerticalAxis]}
                accessibility={accessibility}
                onDragEnd={(event) => void onDragEnd(event)}
              >
                <TableWrap className="rounded-none border-x-0 border-t-0">
                  <Table>
                    <THead>
                      <tr>
                        {/*
                          Unlabelled: the column holds a grip, and every row's own handle
                          carries the name a screen reader needs.

                          This is also where the `sortOrder` number used to be reported,
                          further along the row. It is gone deliberately: the list is
                          SORTED by that number, so printing it too said the same thing
                          twice, and the second telling was the weaker one -- a column of
                          0,1,2,3 that has to be read and compared to learn what the row
                          order already shows at a glance. Dragging is how the order is
                          set now, and position is how it is read.
                        */}
                        <TH className="w-8" aria-label="" />
                        <TH>{t('currency.code')}</TH>
                        <TH>{t('currency.name')}</TH>
                        <TH>{t('currency.symbol')}</TH>
                        <TH>{t('currency.decimals')}</TH>
                        <TH>{t('currency.preview.title')}</TH>
                        <TH>{t('currency.roundingMode')}</TH>
                        <TH>{t('currency.status')}</TH>
                        <TH className="text-end">{t('platform.actions')}</TH>
                      </tr>
                    </THead>
                    <SortableContext
                      items={visible.map((row) => row.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <tbody>
                        {visible.map((row) => {
                          const iso = isoVerdict(row.code, row.decimals);
                          return (
                            <SortableCurrencyRow
                              key={row.id}
                              id={row.id}
                              draggable={canReorder}
                              handleLabel={t('currency.admin.reorder.handle', { code: row.code })}
                            >
                              {(handle) => (
                                <>
                                  <TD className="w-8 pe-0">{handle}</TD>
                                  <TD>
                                    <code className="text-sm font-medium" dir="ltr">
                                      {row.code}
                                    </code>
                                  </TD>
                                  <TD>{row.name}</TD>
                                  <TD>
                                    <span dir="ltr">{row.symbol}</span>
                                  </TD>
                                  <TD>
                                    <span className="tabular-nums">{row.decimals}</span>
                                    {iso.mismatch && (
                                      <span
                                        className="ms-2 inline-flex items-center gap-1 text-xs text-amber-700"
                                        title={t('currency.iso.mismatchBody', {
                                          code: row.code,
                                          expected: iso.expected,
                                          actual: row.decimals,
                                        })}
                                      >
                                        <TriangleAlert className="h-3.5 w-3.5" />
                                        {t('currency.iso.useIso', { expected: iso.expected })}
                                      </span>
                                    )}
                                  </TD>
                                  <TD>
                                    <div className="tabular-nums">
                                      {formatCurrency(ROW_SAMPLE, row.code, i18n.language)}
                                    </div>
                                    <div className="text-xs tabular-nums text-[var(--muted-foreground)]">
                                      {formatAsDefined(ROW_SAMPLE, row, i18n.language)}
                                    </div>
                                  </TD>
                                  <TD className="text-xs">
                                    {t(`currency.rounding.${row.roundingMode}`)}
                                  </TD>
                                  <TD>
                                    <Badge tone={row.isActive ? 'success' : 'neutral'}>
                                      {t(row.isActive ? 'currency.active' : 'currency.inactive')}
                                    </Badge>
                                  </TD>
                                  <TD className="text-end">
                                    {canManage && (
                                      <div className="flex flex-wrap justify-end gap-2">
                                        <Button
                                          size="sm"
                                          variant="secondary"
                                          onClick={() => {
                                            setEditing(row);
                                            setFormOpen(true);
                                          }}
                                        >
                                          {t('common.edit')}
                                        </Button>
                                        {row.isActive ? (
                                          <Button
                                            size="sm"
                                            variant="danger"
                                            onClick={() => setRetiring(row)}
                                          >
                                            {t('currency.admin.retire')}
                                          </Button>
                                        ) : (
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => void restore(row)}
                                          >
                                            {t('currency.admin.restore')}
                                          </Button>
                                        )}
                                      </div>
                                    )}
                                  </TD>
                                </>
                              )}
                            </SortableCurrencyRow>
                          );
                        })}
                      </tbody>
                    </SortableContext>
                  </Table>
                </TableWrap>
              </DndContext>
            </>
          )}
        </CardContent>
      </Card>

      <CurrencyFormDialog
        open={formOpen}
        currency={editing}
        onClose={() => setFormOpen(false)}
        onSaved={load}
      />

      {retiring && (
        <Dialog
          open
          onClose={() => setRetiring(null)}
          title={t('currency.admin.retireTitle', { code: retiring.code })}
          footer={
            <>
              <Button variant="outline" onClick={() => setRetiring(null)}>
                {t('common.cancel')}
              </Button>
              <Button variant="danger" onClick={() => void retire()}>
                {t('currency.admin.retire')}
              </Button>
            </>
          }
        >
          {/* Say why it is a deactivation rather than a delete, in the moment
              the admin is asking for a delete. */}
          <p className="text-sm text-[var(--muted-foreground)]">{t('currency.admin.retireBody')}</p>
        </Dialog>
      )}
    </div>
  );
}
